import {
  Operators,
  Query,
  RuleGroup,
  TableState,
} from "@monolith-forensics/monolith-ui";
import { useCallback, useMemo, useState } from "react";

interface BaseTableQuery {
  pageSize?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  filter?: Query;
}

// Resolves the filter state by removing any incomplete or empty rules.
// Helps prevent unnecessary API queries when the filter set is effectively incomplete or empty.
export const resolveFilterState = (filterState: RuleGroup | undefined) => {
  // Guard against a missing or malformed persisted blob (e.g. a stale
  // `filterState` with no `rules` array). This runs inside the state
  // initializer's try/catch, so throwing here would discard restored
  // sort/search/selection state too, not just the filter.
  if (!filterState || !Array.isArray(filterState.rules)) {
    return undefined;
  }
  // Keep only rules that would actually constrain the query. A rule the user
  // is still building (dimension picked, no value yet) or one whose value has
  // been fully cleared contributes nothing, so we drop it. This prevents an
  // empty rule — e.g. a multiselect deselected down to `[]`, which the API
  // treats as a no-op `IN ()` — from ever reaching the query.
  const filteredRules = filterState.rules.filter((rule) => {
    // A rule with no operator can only come from a hand-edited or corrupted
    // persisted blob (mfui always sets one on addRule). Drop it here rather
    // than let toApiFilter dereference `operator.value` inside the queryFn,
    // which would throw, exhaust react-query's retries, and leave the table
    // errored instead of empty.
    if (!rule?.operator?.value) {
      return false;
    }
    // Valueless operators (is empty / is not empty) are complete on their own.
    if (
      [Operators.IsEmpty.value, Operators.IsNotEmpty.value].includes(
        rule?.operator?.value
      )
    ) {
      return true;
    }
    return (
      Array.isArray(rule.value) &&
      rule.value.length > 0 &&
      !rule.value.some((v) => v === undefined || v === null || v === "")
    );
  });
  if (filteredRules.length > 0) {
    return {
      ...filterState,
      rules: filteredRules,
    };
  }
  return undefined;
};

/**
 * Strips the transient query state (search/filter) from the persisted table
 * blob, keeping the column layout. For grids that share one global
 * `stateStoreKey` across scoped contexts (the per-case Files/Timeline/
 * Preservations tabs), this stops a search or filter entered in one case from
 * leaking into another, while the column layout still follows the user
 * everywhere. Sort is left alone: it never hides rows, and sorting is part of
 * the layout users expect to stick.
 *
 * Call it above `useTableState`: both that hook and the monolith-ui Table read
 * the blob in mount-time state initializers, so the strip has to run first.
 */
export const useClearTransientTableState = (stateStoreKey?: string) => {
  useState(() => {
    if (!stateStoreKey) return null;
    try {
      const raw = localStorage.getItem(stateStoreKey);
      if (!raw) return null;
      const persisted = JSON.parse(raw);
      delete persisted.searchState;
      delete persisted.filterState;
      localStorage.setItem(stateStoreKey, JSON.stringify(persisted));
    } catch (error) {
      console.error("error", error);
    }
    return null;
  });
};

/**
 * Owns the React-state slices a table page needs to drive its data fetch
 * and its row-selection UI:
 *
 * - `filterState` / `searchState` / `sortState` feed into `resolvedQuery`,
 *   which gets passed to the data-fetch query.
 * - `selectionState` tracks user row selections for bulk actions.
 *
 * Does NOT own `columnState`. That lives in the monolith-ui Table instance,
 * read it via `tableInstanceRef.current?.columnState`, and is persisted to
 * localStorage by monolith-ui itself when `stateStorage` is enabled on the
 * Table.
 *
 * `stateStoreKey` is shared with monolith-ui's `stateStorage.key` so the
 * persisted blob (columnState plus filter/search/sort) is read from one place.
 *
 * Slated to be replaced by a reducer-based `useTable` hook in a future
 * refactor.
 */
export const useTableState = <T extends BaseTableQuery>(
  query: T,
  stateStoreKey?: string,
  defaultPageSize?: number
) => {
  const savedTableState: TableState = useMemo(() => {
    try {
      const result = JSON.parse(
        localStorage.getItem(stateStoreKey || "") || "{}"
      );

      result.filterState = resolveFilterState(result.filterState);

      return result;
    } catch (error) {
      console.error("error", error);
      return {};
    }
  }, [stateStoreKey]);

  const [tableState, setTableState] = useState<TableState>(savedTableState);

  const setSelectionState = useCallback(
    (selectionState: TableState["selectionState"]) => {
      setTableState((prev) => ({ ...prev, selectionState }));
    },
    []
  );

  const setFilterState = useCallback((filter: TableState["filterState"]) => {
    setTableState((prev) => {
      // Skip the re-render when nothing actually changed. Common while typing a
      // first rule: each debounced emission resolves to `undefined`, and a bare
      // `undefined -> undefined` update would still churn the whole table.
      if (prev.filterState === filter) {
        return prev;
      }
      return { ...prev, filterState: filter };
    });
  }, []);

  const setSearchState = useCallback((search: TableState["searchState"]) => {
    setTableState((prev) => ({ ...prev, searchState: search }));
  }, []);

  const setSortState = useCallback((sort: TableState["sortState"]) => {
    setTableState((prev) => ({ ...prev, sortState: sort }));
  }, []);

  const _query: T = {
    pageSize: defaultPageSize,
    search: tableState.searchState,
    sort_by: tableState.sortState?.dataField,
    sort_dir: tableState.sortState?.dir,
    filter: tableState.filterState,
    ...query,
  };

  return {
    resolvedQuery: _query,
    selectionState: tableState.selectionState,
    setSelectionState,
    filterState: tableState.filterState,
    setFilterState,
    search: tableState.searchState,
    setSearchState,
    sortState: tableState.sortState,
    setSortState,
  };
};
